
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { sanitizeHtml, validateAndSanitizeInput } from "@/utils/sanitization";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Edit, Trash2, Eye, EyeOff } from "lucide-react";

interface Banner {
  id: string;
  title: string | null;
  content: string | null;
  banner_type: 'text' | 'image';
  image_url: string | null;
  background_color: string;
  text_color: string;
  is_active: boolean;
  created_at: string;
}

const BannerManager = () => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    banner_type: 'text' as 'text' | 'image',
    image_url: '',
    background_color: '#f3f4f6',
    text_color: '#374151',
    is_active: false
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const { data, error } = await supabase
        .from('banners')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching banners:', error);
        toast({
          title: "Error",
          description: "Failed to fetch banners",
          variant: "destructive",
        });
        return;
      }

      setBanners(data || []);
    } catch (error) {
      console.error('Error fetching banners:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingBanner) {
        const { error } = await supabase
          .from('banners')
          .update(formData)
          .eq('id', editingBanner.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Banner updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('banners')
          .insert([formData]);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Banner created successfully",
        });
      }

      resetForm();
      fetchBanners();
    } catch (error) {
      console.error('Error saving banner:', error);
      toast({
        title: "Error",
        description: "Failed to save banner",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this banner?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('banners')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Banner deleted successfully",
      });
      
      fetchBanners();
    } catch (error) {
      console.error('Error deleting banner:', error);
      toast({
        title: "Error",
        description: "Failed to delete banner",
        variant: "destructive",
      });
    }
  };

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('banners')
        .update({ is_active: !isActive })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Banner ${!isActive ? 'activated' : 'deactivated'} successfully`,
      });
      
      fetchBanners();
    } catch (error) {
      console.error('Error toggling banner:', error);
      toast({
        title: "Error",
        description: "Failed to update banner",
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      banner_type: 'text',
      image_url: '',
      background_color: '#f3f4f6',
      text_color: '#374151',
      is_active: false
    });
    setEditingBanner(null);
    setShowForm(false);
  };

  const handleEdit = (banner: Banner) => {
    setFormData({
      title: banner.title || '',
      content: banner.content || '',
      banner_type: banner.banner_type,
      image_url: banner.image_url || '',
      background_color: banner.background_color,
      text_color: banner.text_color,
      is_active: banner.is_active
    });
    setEditingBanner(banner);
    setShowForm(true);
  };

  if (isLoading) {
    return <div>Loading banners...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Banner Management</h2>
        <Button onClick={() => setShowForm(true)} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Add Banner
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingBanner ? 'Edit Banner' : 'Create New Banner'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Title (Optional)</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Banner title (optional)"
                />
              </div>

              <div>
                <Label htmlFor="banner_type">Banner Type</Label>
                <select
                  id="banner_type"
                  value={formData.banner_type}
                  onChange={(e) => setFormData({ ...formData, banner_type: e.target.value as 'text' | 'image' })}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="text">Text Banner</option>
                  <option value="image">Image Banner</option>
                </select>
              </div>

              {formData.banner_type === 'text' && (
                <div>
                  <Label htmlFor="content">Content</Label>
                  <RichTextEditor
                    value={formData.content}
                    onChange={(value) => setFormData({ ...formData, content: value })}
                    placeholder="Banner content"
                    height="200px"
                  />
                </div>
              )}

              {formData.banner_type === 'image' && (
                <div>
                  <Label htmlFor="image_url">Image URL</Label>
                  <Input
                    id="image_url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="https://example.com/image.png"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="background_color">Background Color</Label>
                  <Input
                    id="background_color"
                    type="color"
                    value={formData.background_color}
                    onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="text_color">Text Color</Label>
                  <Input
                    id="text_color"
                    type="color"
                    value={formData.text_color}
                    onChange={(e) => setFormData({ ...formData, text_color: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>

              <div className="flex gap-2">
                <Button type="submit">
                  {editingBanner ? 'Update Banner' : 'Create Banner'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {banners.map((banner) => (
          <Card key={banner.id} className={banner.is_active ? 'border-green-500' : ''}>
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-semibold">{banner.title || 'Untitled Banner'}</h3>
                  <p className="text-sm text-gray-600 capitalize">{banner.banner_type} Banner</p>
                  {banner.content && (
                    <div 
                      className="mt-1 rich-text-content prose prose-sm max-w-none text-sm" 
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(banner.content) }}
                    />
                  )}
                  {banner.image_url && <p className="text-sm mt-1 text-blue-600">{banner.image_url}</p>}
                  <div className="flex gap-4 mt-2">
                    <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: banner.background_color, color: banner.text_color }}>
                      Preview Colors
                    </span>
                    <span className={`text-xs px-2 py-1 rounded ${banner.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {banner.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggleActive(banner.id, banner.is_active)}
                  >
                    {banner.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(banner)}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(banner.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {banners.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-gray-500">No banners created yet. Click "Add Banner" to create your first banner.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BannerManager;
